import { useState } from 'react';
import Select from 'react-select';
import { useLoading } from '../../../components/globalloaders/LoadingContext';
import { toast } from 'react-toastify';
import { Bars3Icon, CurrencyDollarIcon, ReceiptPercentIcon } from "@heroicons/react/24/outline";
import { expenseTypeOptions } from '../../../constants/options';
import { encryptPayload, formatCurrency, capitalizeWords, toDateTimeIso } from '../../../constants/helpers';
import ConfirmDialog from '../../../components/dialogbox/ConfirmDialog';
import api from '../../../utils/auth/api';

const Finance = ({ setIsMobileOpen }) => {

    const { isLoading, setIsLoading } = useLoading();

    const [dialog, setDialog] = useState({
        open: false,
        type: "info",
        title: "",
        message: "",
        confirmText: "",
        action: () => { },
    });

    // Page Mode State
    const [pageMode, setPageMode] = useState("serviceFee");

    // Add DialogBox Code & Store API
    const initialFormState = {
        clientAddress: '',
        type: '',
        date: '',
        amount: '',
        note: '',
    };
    const [formData, setFormData] = useState(initialFormState);
    const handleSelectChange = (selectedOption, { name }) => {
        setFormData({
            ...formData,
            [name]: selectedOption ? selectedOption.value : "",
        });
    };
    const handleNumberChange = (e) => {
        const { name, value } = e.target;
        const digits = value.replace(/\D/g, "");
        setFormData(prev => ({ ...prev, [name]: digits }));
    };
    const onChange = (e) => {
        const { name, value } = e.target;
        const autoCapFields = ["clientAddress"];
        setFormData({
            ...formData,
            [name]: autoCapFields.includes(name) ? capitalizeWords(value) : value,
        });
    };
    const handleSubmit = async () => {

        if (isLoading) return;

        // --- Required field checks ---
        if (pageMode === "serviceFee" && !formData.clientAddress) {
            toast.warning("Client Address is required.");
            return;
        }
        if (pageMode === "expense" && !formData.type) {
            toast.warning("Expense Type is required.");
            return;
        }
        if (!formData.date) {
            toast.warning("Date is required.");
            return;
        }
        if (!formData.amount) {
            toast.warning("Amount is required.");
            return;
        }

        const payload = {
            date: toDateTimeIso(formData.date),
            amount: Number(formData.amount),
            ...(pageMode === "serviceFee" && { clientAddress: formData.clientAddress }),
            ...(pageMode === "expense" && { type: formData.type, note: formData.note?.trim() || null }),
        };
        const encryptedData = encryptPayload(payload);

        setIsLoading(true);
        try {
            const response = await api.post(`${process.env.REACT_APP_URI}/finance/create/${pageMode}`, { data: encryptedData });
            if (response.data.success) {
                toast.success(response.data.message);
                setFormData(initialFormState);
            }
            else {
                toast.error(response.data.message);
            }
        }
        catch (error) {
            console.log("Finance Creation failed:", error);
            if (error.response && error.response.data) {
                const data = error.response.data;
                if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
                    data.errors.forEach(err => toast.warning(err));
                } else {
                    toast.error(data.message);
                }
            } else {
                toast.error("Unable to Create Finance Network Error, please try again!");
            }
        }
        finally {
            setIsLoading(false);
            return;
        }
    };

    return (
        <>
            <ConfirmDialog
                isOpen={dialog.open}
                onClose={() => setDialog((d) => ({ ...d, open: false }))}
                onConfirm={dialog.action}
                title={dialog.title}
                message={dialog.message}
                confirmText={dialog.confirmText}
                type={dialog.type}
            />
            <div className="w-full">
                {/* Heading */}
                <div className="w-full mb-5 flex flex-wrap items-center justify-between gap-2 sm:gap-4">
                    <div className="flex items-center space-x-2 sm:space-x-3">
                        <button
                            className="md:hidden p-2 bg-white shadow rounded-lg"
                            onClick={() => setIsMobileOpen(true)}
                        >
                            <Bars3Icon className="h-5 w-5 sm:h-6 sm:w-6 text-gray-800" />
                        </button>
                        <div className="flex flex-wrap items-center">
                            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-wide text-blue-600">
                                Fina
                            </h1>
                            <span className="text-2xl sm:text-3xl font-extrabold tracking-wide text-gray-800">
                                nce
                            </span>
                        </div>
                    </div>

                    <div className="flex bg-gray-100 p-1 rounded-xl shadow-sm w-fit">
                        <button
                            onClick={() => setPageMode("serviceFee")}
                            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition
                                    ${pageMode === "serviceFee"
                                    ? "bg-blue-600 text-white shadow"
                                    : "text-gray-600"
                                }`}
                        >
                            <CurrencyDollarIcon className="h-5 w-5" />
                            <span className="hidden sm:inline">Service Fee</span>
                        </button>
                        <button
                            onClick={() => setPageMode("expense")}
                            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition
                                    ${pageMode === "expense"
                                    ? "bg-blue-600 text-white shadow"
                                    : "text-gray-600"
                                }`}
                        >
                            <ReceiptPercentIcon className="h-5 w-5" />
                            <span className="hidden sm:inline">Expense</span>
                        </button>
                    </div>
                </div>

                <hr className="border-t border-gray-200 my-4" />

                {/* Data Creation Code */}
                <div className="w-full bg-white rounded-2xl border border-gray-200 p-4 space-y-4">
                    <div className='flex flex-col items-center'>
                        <h2 className="text-xl sm:text-2xl font-semibold text-gray-800">
                            Create {pageMode === "serviceFee" ? "Service Fee" : "Expense"}
                        </h2>
                        <p className="text-sm text-gray-500">
                            Add new {pageMode === "serviceFee" ? "service fee" : "expense"} details below.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 gap-4">
                        {pageMode === "serviceFee" && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Client Address</label>
                                <input className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
                                    placeholder="H#01, ST#01, ABC..."
                                    type="text"
                                    name='clientAddress'
                                    value={formData.clientAddress}
                                    onChange={onChange}
                                />
                            </div>
                        )}

                        {pageMode === "expense" && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Type</label>
                                <Select
                                    name="type"
                                    options={expenseTypeOptions}
                                    placeholder="Select Type"
                                    value={formData.type ? expenseTypeOptions.find(option => option.value === formData.type) : null}
                                    onChange={(selectedOption) =>
                                        handleSelectChange(selectedOption, { name: 'type' })
                                    }
                                />
                            </div>
                        )}

                        <div>
                            <label className="block text-sm font-medium text-gray-700">Date</label>
                            <input className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
                                type="datetime-local"
                                name='date'
                                value={formData.date}
                                onChange={onChange}
                            />
                        </div>

                        <div className="space-y-1">
                            <label className="block text-sm font-medium text-gray-700">Amount</label>
                            <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
                                    PKR
                                </span>
                                <input
                                    className="w-full pl-12 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
                                    placeholder="X,XXX"
                                    type="text"
                                    name="amount"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    value={formatCurrency(formData.amount)}
                                    onChange={handleNumberChange}
                                />
                            </div>
                        </div>

                        {pageMode === "expense" && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Note</label>
                                <input className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
                                    type="text"
                                    placeholder='(Optional)'
                                    name='note'
                                    value={formData.note}
                                    onChange={onChange}
                                />
                            </div>
                        )}
                    </div>
                    <div className="border-t pt-3 flex justify-between">
                        <button
                            type='button'
                            onClick={() => setFormData(initialFormState)}
                            className="px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base font-medium text-gray-600 bg-gray-200 hover:bg-gray-300 transition"
                        >
                            Reset
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setDialog({
                                    open: true,
                                    type: "warning",
                                    title: `Confirm ${pageMode === "serviceFee" ? "Service Fee" : "Expense"} Creation`,
                                    message: `Please confirm all information is correct before creating this ${pageMode === "serviceFee" ? "service fee" : "expense"} record.`,
                                    confirmText: "Confirm",
                                    action: () => handleSubmit(),
                                })
                            }}
                            className="px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base bg-blue-600 text-white hover:bg-blue-700 transition"
                        >
                            Create
                        </button>
                    </div>
                </div>
            </div>
        </>
    )
}

export default Finance;